import React, { useRef, useCallback } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Download, Share2, Copy, Check } from 'lucide-react';
import html2canvas from 'html2canvas';

// TryOn Studio Logo URL
const TRYON_STUDIO_LOGO = 'https://v1-garments.s3.ap-south-1.amazonaws.com/nanokart_ai/20260128073408.webp';

/**
 * Branded QR Code Component
 * 
 * Generates a QR code with custom branding including:
 * - TryOn Studio logo (top left)
 * - Partner/Business logo (if available, shown with business name)
 * - Partner/Business name
 * - Product/Item name
 * - Optional product thumbnail
 * - "Powered by Nanokart.AI" footer
 * 
 * Features:
 * - Download as PNG image
 * - Copy link to clipboard
 * - Share via Web Share API
 */
export const BrandedQRCode = ({
  url,
  businessName,
  businessLogoUrl,
  productName,
  productImageUrl,
  linkId,
  size = 200,
  showActions = true,
  onCopy,
  onDownload,
  className = ""
}) => {
  const qrContainerRef = useRef(null);
  const [copied, setCopied] = React.useState(false);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      onCopy?.();
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }, [url, onCopy]);

  const handleDownload = useCallback(async () => {
    if (!qrContainerRef.current) return;

    try {
      // Create a canvas
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Set canvas dimensions
      const padding = 32;
      const qrSize = size;
      const tryonLogoSize = 36;
      const businessLogoSize = 22;
      const thumbnailWidth = 50;
      const thumbnailHeight = 65;
      
      // Calculate height based on whether we have a product image
      const headerHeight = productImageUrl ? 140 : 70;
      const footerHeight = 50;
      const canvasWidth = qrSize + padding * 2;
      const canvasHeight = headerHeight + qrSize + footerHeight + padding;
      
      canvas.width = canvasWidth;
      canvas.height = canvasHeight;
      
      // Fill white background
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvasWidth, canvasHeight);
      
      // Draw rounded border
      ctx.strokeStyle = '#e2e8f0';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.roundRect(8, 8, canvasWidth - 16, canvasHeight - 16, 12);
      ctx.stroke();
      
      // Helper function to load image
      const loadImage = (src) => {
        return new Promise((resolve) => {
          if (!src) {
            resolve(null);
            return;
          }
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload = () => resolve(img);
          img.onerror = () => {
            console.warn('Failed to load image:', src);
            resolve(null);
          };
          img.src = src;
        });
      };
      
      // Load all images in parallel
      const [tryonLogoImg, businessLogoImg, productImg] = await Promise.all([
        loadImage(TRYON_STUDIO_LOGO),
        loadImage(businessLogoUrl),
        loadImage(productImageUrl)
      ]);
      
      // 1. Draw TryOn Studio logo (top left)
      if (tryonLogoImg) {
        ctx.drawImage(tryonLogoImg, padding - 8, padding - 8, tryonLogoSize, tryonLogoSize);
      }
      
      // 2. Draw header text
      let yOffset = padding + 28;
      
      // Business name with logo
      if (businessName) {
        ctx.fillStyle = '#1e293b';
        ctx.font = 'bold 15px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
        ctx.textAlign = 'center';
        
        if (businessLogoImg) {
          // Draw business logo and name together (centered)
          const textWidth = ctx.measureText(businessName).width;
          const totalWidth = businessLogoSize + 6 + textWidth;
          const startX = (canvasWidth - totalWidth) / 2;
          
          ctx.drawImage(businessLogoImg, startX, yOffset - businessLogoSize + 5, businessLogoSize, businessLogoSize);
          ctx.textAlign = 'left';
          ctx.fillText(businessName, startX + businessLogoSize + 6, yOffset);
          ctx.textAlign = 'center';
        } else {
          ctx.fillText(businessName, canvasWidth / 2, yOffset);
        }
        yOffset += 22;
      }
      
      // Product name (blue)
      if (productName) {
        ctx.fillStyle = '#2563eb';
        ctx.font = '13px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(productName, canvasWidth / 2, yOffset);
        yOffset += 18;
      }
      
      // 3. Draw product thumbnail (if available)
      if (productImg) {
        const thumbX = (canvasWidth - thumbnailWidth) / 2;
        const thumbY = yOffset + 4;
        
        // Draw thumbnail border
        ctx.strokeStyle = '#e2e8f0';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.roundRect(thumbX - 2, thumbY - 2, thumbnailWidth + 4, thumbnailHeight + 4, 6);
        ctx.stroke();
        
        // Draw thumbnail with clipping for rounded corners
        ctx.save();
        ctx.beginPath();
        ctx.roundRect(thumbX, thumbY, thumbnailWidth, thumbnailHeight, 4);
        ctx.clip();
        ctx.drawImage(productImg, thumbX, thumbY, thumbnailWidth, thumbnailHeight);
        ctx.restore();
      }
      
      // 4. Draw QR code from SVG
      const svgElement = qrContainerRef.current.querySelector('svg');
      if (svgElement) {
        const svgClone = svgElement.cloneNode(true);
        svgClone.setAttribute('width', qrSize);
        svgClone.setAttribute('height', qrSize);
        
        const svgData = new XMLSerializer().serializeToString(svgClone);
        const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
        const svgUrl = URL.createObjectURL(svgBlob);
        
        const qrImg = new Image();
        
        await new Promise((resolve) => {
          qrImg.onload = () => {
            const qrX = (canvasWidth - qrSize) / 2;
            const qrY = headerHeight + 8;
            
            // QR code border
            ctx.strokeStyle = '#f1f5f9';
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.roundRect(qrX - 6, qrY - 6, qrSize + 12, qrSize + 12, 6);
            ctx.stroke();
            
            // Draw QR code
            ctx.drawImage(qrImg, qrX, qrY, qrSize, qrSize);
            
            URL.revokeObjectURL(svgUrl);
            resolve();
          };
          qrImg.onerror = () => {
            URL.revokeObjectURL(svgUrl);
            resolve();
          };
          qrImg.src = svgUrl;
        });
      }
      
      // 5. Draw footer
      const footerY = canvasHeight - 38;
      
      // Link ID
      if (linkId) {
        ctx.fillStyle = '#94a3b8';
        ctx.font = '11px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('Link ID:', canvasWidth / 2 - 28, footerY);
        
        ctx.fillStyle = '#2563eb';
        ctx.font = 'bold 11px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
        ctx.fillText(linkId, canvasWidth / 2 + 18, footerY);
      }
      
      // Powered by text
      ctx.fillStyle = '#94a3b8';
      ctx.font = '10px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('Powered by Nanokart.AI', canvasWidth / 2, footerY + 18);
      
      // Download
      const link = document.createElement('a');
      link.download = `qr-${linkId || productName || 'code'}-${Date.now()}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      
      onDownload?.();
      
    } catch (err) {
      console.error('Failed to download QR:', err);
    }
  }, [size, businessName, businessLogoUrl, productName, productImageUrl, linkId, onDownload]);

  const handleShare = useCallback(async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Try on ${productName}`,
          text: `Virtual try-on for ${productName} from ${businessName}`,
          url: url
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Share failed:', err);
        }
      }
    } else {
      handleCopy();
    }
  }, [url, productName, businessName, handleCopy]);

  return (
    <div className={`bg-white rounded-2xl border border-slate-200 overflow-hidden ${className}`}>
      {/* QR Code Container */}
      <div ref={qrContainerRef} className="p-6 text-center relative">
        {/* TryOn Studio Logo - Top Left (increased size by 25%) */}
        <div className="absolute top-4 left-4">
          <img 
            src={TRYON_STUDIO_LOGO} 
            alt="TryOn Studio" 
            className="w-10 h-10 object-contain"
          />
        </div>
        
        {/* Header */}
        <div className="mb-4">
          {businessName && (
            <div className="flex items-center justify-center gap-2">
              {businessLogoUrl && (
                <img 
                  src={businessLogoUrl} 
                  alt={businessName} 
                  className="w-7 h-7 object-contain rounded"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              )}
              <h3 className="text-lg font-semibold text-slate-800">{businessName}</h3>
            </div>
          )}
          {productName && (
            <p className="text-sm text-blue-600 mt-1">{productName}</p>
          )}
        </div>

        {/* Product Thumbnail */}
        {productImageUrl && (
          <div className="mb-4 flex justify-center">
            <img 
              src={productImageUrl} 
              alt={productName} 
              className="w-16 h-20 object-cover rounded-lg border border-slate-200"
            />
          </div>
        )}

        {/* QR Code */}
        <div className="inline-block p-3 bg-white rounded-xl border border-slate-100">
          <QRCodeSVG 
            value={url}
            size={size}
            level="M"
            includeMargin={false}
            fgColor="#1e293b"
            bgColor="#ffffff"
          />
        </div>

        {/* Link ID */}
        {linkId && (
          <div className="mt-3 flex items-center justify-center gap-1.5">
            <span className="text-xs text-slate-400">Link ID:</span>
            <span className="text-xs font-mono font-semibold text-blue-600 bg-blue-50 px-2 py-0.5 rounded">{linkId}</span>
          </div>
        )}

        {/* Footer */}
        <p className="mt-3 text-xs text-slate-400">Powered by Nanokart.AI</p>
      </div>

      {/* Actions */}
      {showActions && (
        <div className="px-4 pb-4 flex gap-2">
          <button
            onClick={handleCopy}
            className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors text-sm font-medium"
          >
            {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
            {copied ? 'Copied!' : 'Copy Link'}
          </button>
          <button
            onClick={handleDownload}
            className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm font-medium"
          >
            <Download className="w-4 h-4" />
            Download QR
          </button>
          {navigator.share && (
            <button
              onClick={handleShare}
              className="px-3 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors"
              title="Share"
            >
              <Share2 className="w-4 h-4" />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Simple QR Code Component (without branding)
 * For cases where you just need a basic QR code
 */
export const SimpleQRCode = ({ url, size = 128, className = "" }) => {
  return (
    <div className={`inline-block p-2 bg-white rounded-lg ${className}`}>
      <QRCodeSVG 
        value={url}
        size={size}
        level="M"
        includeMargin={false}
        fgColor="#1e293b"
        bgColor="#ffffff"
      />
    </div>
  );
};

/**
 * QR Code Modal Component
 * Opens a modal with the branded QR code
 */
export const QRCodeModal = ({
  isOpen,
  onClose,
  url,
  businessName,
  businessLogoUrl,
  productName,
  productImageUrl,
  linkId
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full animate-in zoom-in-95 duration-200">
        {/* Close button */}
        <div className="flex justify-end p-3 pb-0">
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <BrandedQRCode
          url={url}
          businessName={businessName}
          businessLogoUrl={businessLogoUrl}
          productName={productName}
          productImageUrl={productImageUrl}
          linkId={linkId}
          size={180}
          showActions={true}
        />
      </div>
    </div>
  );
};

export default BrandedQRCode;
